import { supabase } from '../lib/supabase';

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in kilometers
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Get distance label in Arabic
 */
export const getDistanceLabel = (distanceKm) => {
  if (distanceKm < 5) {
    return 'قريب جدًا';
  } else if (distanceKm < 15) {
    return 'قريب';
  } else if (distanceKm < 30) {
    return 'متوسط';
  } else {
    return 'بعيد';
  }
};

/**
 * Get nearest photographers based on location
 * Uses server-side function if available, otherwise client-side calculation
 */
export const locationService = {
  /**
   * Get nearest photographers
   * @param {Object} params - { lat, lng, city, category, limit }
   * @returns {Array} Array of photographers with distance
   */
  async getNearestPhotographers({ lat, lng, city, category, limit = 5 }) {
    if (!lat || !lng || !city || !category) {
      throw new Error('Missing required parameters: lat, lng, city, category');
    }

    try {
      // Try server-side function first
      const { data: serverResults, error: serverError } = await supabase
        .rpc('get_nearest_photographers', {
          p_lat: parseFloat(lat),
          p_lng: parseFloat(lng),
          p_city: city,
          p_category: category,
          p_limit: limit
        });

      if (!serverError && serverResults && serverResults.length > 0) {
        // Format server results
        return serverResults.map(photographer => ({
          ...photographer,
          distanceKm: parseFloat(photographer.distance_km || 0),
          distanceLabel: getDistanceLabel(photographer.distance_km || 0)
        }));
      }

      // Fallback to client-side calculation
      console.log('Server function not available, using client-side calculation');
      return await this.getNearestPhotographersClientSide({ lat, lng, city, category, limit });
    } catch (error) {
      console.error('Error getting nearest photographers:', error);
      // Fallback to client-side
      return await this.getNearestPhotographersClientSide({ lat, lng, city, category, limit });
    }
  },

  /**
   * Client-side calculation (fallback)
   */
  async getNearestPhotographersClientSide({ lat, lng, city, category, limit = 5 }) {
    // Get all photographers in the city with the category
    const { data: photographers, error } = await supabase
      .from('photographer_profiles')
      .select(`
        id,
        user_id,
        base_lat,
        base_lng,
        base_address,
        city,
        specializations,
        rating,
        total_bookings,
        available_for_booking,
        user:user_profiles!inner(id, full_name, avatar_url, is_active)
      `)
      .eq('city', city)
      .eq('available_for_booking', true)
      .not('base_lat', 'is', null)
      .not('base_lng', 'is', null);

    if (error) {
      console.error('Error fetching photographers:', error);
      throw error;
    }

    if (!photographers || photographers.length === 0) {
      return [];
    }

    // Filter by category and calculate distances
    const photographersWithDistance = photographers
      .filter(p => {
        // Check if photographer has the category in specializations
        if (!p.specializations || p.specializations.length === 0) {
          return true; // Include if no specializations (general photographer)
        }
        return p.specializations.includes(category) || 
               p.specializations.some(spec => spec.toLowerCase().includes(category.toLowerCase()));
      })
      .filter(p => p.user?.is_active !== false)
      .map(photographer => {
        const distanceKm = calculateDistance(
          parseFloat(lat),
          parseFloat(lng),
          parseFloat(photographer.base_lat),
          parseFloat(photographer.base_lng)
        );

        return {
          photographer_id: photographer.id,
          user_id: photographer.user_id,
          distanceKm: Math.round(distanceKm * 10) / 10, // Round to 1 decimal
          distanceLabel: getDistanceLabel(distanceKm),
          base_lat: photographer.base_lat,
          base_lng: photographer.base_lng,
          base_address: photographer.base_address,
          city: photographer.city,
          specializations: photographer.specializations,
          rating: photographer.rating,
          total_bookings: photographer.total_bookings,
          available_for_booking: photographer.available_for_booking,
          user: photographer.user
        };
      })
      .sort((a, b) => a.distanceKm - b.distanceKm) // Sort by distance
      .slice(0, limit); // Limit results

    return photographersWithDistance;
  },

  /**
   * Update photographer base location
   */
  async updatePhotographerLocation(photographerId, { lat, lng, address }) {
    const { data, error } = await supabase
      .from('photographer_profiles')
      .update({
        base_lat: parseFloat(lat),
        base_lng: parseFloat(lng),
        base_address: address
      })
      .eq('user_id', photographerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};
